import React, { useState } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import EssaySlotSyncService from '../../services/EssaySlotSyncService';
import { RefreshCw, CheckCircle, AlertCircle, Users, Plus, Edit3 } from 'lucide-react';

/**
 * EssayTaskSyncAdmin Component
 *
 * Allows admins to manually trigger essay task generation and sync
 * Useful for testing, recovery, and initialization
 */
const EssayTaskSyncAdmin = () => {
  const { familyUsers } = useFamilyContext();
  const [syncing, setSyncing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const children = familyUsers?.filter(user => user.type !== 'parent') || [];

  const handleSync = async (userId) => {
    try {
      setSyncing(true);
      setError(null);
      setResults(null);

      console.log(`[Essay Sync Admin] Starting sync for user ${userId}`);

      // SYNC WITH: EssayManagementAdmin.jsx (uses EssaySlotSyncService)
      // This creates/updates essay_slots, then database trigger creates tasks
      const result = await EssaySlotSyncService.syncAllEssaySlotsForUser(userId);

      console.log(`[Essay Sync Admin] Sync complete:`, result);

      setResults({
        userId,
        created: result.totalCreated,
        updated: result.totalUpdated,
        total: result.totalCreated + result.totalUpdated,
        colleges: result.colleges,
        timestamp: new Date().toISOString()
      });

      setSyncing(false);
    } catch (err) {
      console.error('[Essay Sync Admin] Sync failed:', err);
      setError(err.message || 'Failed to sync essay tasks');
      setSyncing(false);
    }
  };

  const handleSyncAll = async () => {
    try {
      setSyncing(true);
      setError(null);
      setResults(null);

      const allResults = [];

      for (const child of children) {
        try {
          // SYNC WITH: EssayManagementAdmin.jsx (uses EssaySlotSyncService)
          // This creates/updates essay_slots, then database trigger creates tasks
          const result = await EssaySlotSyncService.syncAllEssaySlotsForUser(child.id);
          allResults.push({
            userId: child.id,
            userName: child.name || child.displayName,
            inserted: result.totalCreated,
            updated: result.totalUpdated,
            total: result.totalCreated + result.totalUpdated,
            colleges: result.colleges
          });
        } catch (err) {
          console.error(`Failed to sync user ${child.id}:`, err);
          allResults.push({
            userId: child.id,
            userName: child.name || child.displayName,
            error: err.message
          });
        }
      }

      setResults({
        bulkSync: true,
        results: allResults,
        timestamp: new Date().toISOString()
      });

      setSyncing(false);
    } catch (err) {
      console.error('[Essay Sync Admin] Bulk sync failed:', err);
      setError(err.message || 'Failed to sync all users');
      setSyncing(false);
    }
  };

  const handleGenerateMissingTasks = async (userId) => {
    try {
      setSyncing(true);
      setError(null);
      setResults(null);

      console.log(`[Essay Sync Admin] Generating missing timeline tasks for user ${userId}`);

      const result = await EssaySlotSyncService.generateMissingTimelineTasks(userId);

      console.log(`[Essay Sync Admin] Generation complete:`, result);

      setResults({
        userId,
        type: 'generate',
        tasksCreated: result.tasksCreated,
        essaysProcessed: result.essaysProcessed,
        colleges: result.colleges,
        timestamp: new Date().toISOString()
      });

      setSyncing(false);
    } catch (err) {
      console.error('[Essay Sync Admin] Task generation failed:', err);
      setError(err.message || 'Failed to generate timeline tasks');
      setSyncing(false);
    }
  };

  const handleUpdateTaskTitles = async (userId) => {
    try {
      setSyncing(true);
      setError(null);
      setResults(null);

      console.log(`[Essay Sync Admin] Updating task titles for user ${userId}`);

      const result = await EssaySlotSyncService.updateEssayTaskTitles(userId);

      console.log(`[Essay Sync Admin] Update complete:`, result);

      setResults({
        userId,
        type: 'update_titles',
        tasksUpdated: result.tasksUpdated,
        essaysProcessed: result.essaysProcessed,
        colleges: result.colleges,
        timestamp: new Date().toISOString()
      });

      setSyncing(false);
    } catch (err) {
      console.error('[Essay Sync Admin] Title update failed:', err);
      setError(err.message || 'Failed to update task titles');
      setSyncing(false);
    }
  };

  const handleUpdateAllFromTemplates = async (userId) => {
    try {
      setSyncing(true);
      setError(null);
      setResults(null);

      console.log(`[Essay Sync Admin] Updating ALL task fields from templates for user ${userId}`);

      const result = await EssaySlotSyncService.updateEssayTasksFromTemplates(userId);

      console.log(`[Essay Sync Admin] Template update complete:`, result);

      setResults({
        userId,
        type: 'update_from_templates',
        tasksUpdated: result.tasksUpdated,
        essaysProcessed: result.essaysProcessed,
        colleges: result.colleges,
        timestamp: new Date().toISOString()
      });

      setSyncing(false);
    } catch (err) {
      console.error('[Essay Sync Admin] Template update failed:', err);
      setError(err.message || 'Failed to update tasks from templates');
      setSyncing(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Essay Task Sync & Timeline Management
        </h3>
        <p className="text-sm text-slate-600 mb-2">
          Manage essay slots and timeline tasks:
        </p>
        <div className="grid grid-cols-3 gap-4 text-sm text-slate-600">
          <div>
            <div className="font-semibold text-indigo-700 mb-1">Sync Essay Slots:</div>
            <ul className="list-disc list-inside space-y-0.5 text-xs">
              <li>Create essay_slots from requirements</li>
              <li>Sync with global college data</li>
            </ul>
          </div>
          <div>
            <div className="font-semibold text-emerald-700 mb-1">Generate Timeline Tasks:</div>
            <ul className="list-disc list-inside space-y-0.5 text-xs">
              <li>Create 6-7 tasks per essay</li>
              <li>Only for essays missing tasks</li>
            </ul>
          </div>
          <div>
            <div className="font-semibold text-amber-700 mb-1">Update Task Titles:</div>
            <ul className="list-disc list-inside space-y-0.5 text-xs">
              <li>Refresh titles with friendly_title</li>
              <li>Update metadata with current prompt</li>
            </ul>
          </div>
          <div>
            <div className="font-semibold text-rose-700 mb-1">🔥 Update From Templates:</div>
            <ul className="list-disc list-inside space-y-0.5 text-xs">
              <li>Updates ALL fields from templates</li>
              <li>Priority, module_link, workflow, tips</li>
            </ul>
          </div>
          <div>
            <div className="font-semibold text-violet-700 mb-1">Sync All Users:</div>
            <ul className="list-disc list-inside space-y-0.5 text-xs">
              <li>Batch process all students</li>
              <li>Comprehensive sync</li>
            </ul>
          </div>
        </div>
      </div>

      {/* User Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-slate-700 mb-3">
          Select Student
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {children.map(child => (
            <button
              key={child.id}
              onClick={() => setSelectedUserId(child.id)}
              disabled={syncing}
              className={`px-4 py-3 rounded-lg border-2 transition-all ${
                selectedUserId === child.id
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-900 font-semibold'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-indigo-50'
              } ${syncing ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {child.name || child.displayName}
            </button>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-3 mb-6">
        <div className="flex gap-3">
          <button
            onClick={() => selectedUserId && handleSync(selectedUserId)}
            disabled={!selectedUserId || syncing}
            className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {syncing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Syncing...</span>
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                <span>Sync Essay Slots</span>
              </>
            )}
          </button>

          <button
            onClick={handleSyncAll}
            disabled={children.length === 0 || syncing}
            className="flex-1 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {syncing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Syncing All...</span>
              </>
            ) : (
              <>
                <Users className="w-4 h-4" />
                <span>Sync All Users</span>
              </>
            )}
          </button>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => selectedUserId && handleGenerateMissingTasks(selectedUserId)}
            disabled={!selectedUserId || syncing}
            className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {syncing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Generating...</span>
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                <span>Generate Missing Timeline Tasks</span>
              </>
            )}
          </button>

          <button
            onClick={() => selectedUserId && handleUpdateTaskTitles(selectedUserId)}
            disabled={!selectedUserId || syncing}
            className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {syncing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Updating...</span>
              </>
            ) : (
              <>
                <Edit3 className="w-4 h-4" />
                <span>Update Task Titles</span>
              </>
            )}
          </button>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => selectedUserId && handleUpdateAllFromTemplates(selectedUserId)}
            disabled={!selectedUserId || syncing}
            className="w-full px-4 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-semibold"
          >
            {syncing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Updating All Fields...</span>
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                <span>🔥 Update ALL Fields From Templates (7th step, modules, etc.)</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="font-medium">Sync Failed</span>
          </div>
          <p className="mt-2 text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Results Display */}
      {results && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
          <div className="flex items-center gap-2 text-emerald-700 mb-3">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span className="font-semibold">Sync Complete</span>
          </div>

          {results.bulkSync ? (
            // Bulk sync results
            <div className="space-y-3">
              <p className="text-sm text-emerald-700">
                Synced {results.results.length} user{results.results.length !== 1 ? 's' : ''}
              </p>
              <div className="space-y-2">
                {results.results.map((userResult, index) => (
                  <div key={index} className="bg-white rounded p-3 text-sm">
                    <div className="font-medium text-slate-900 mb-1">
                      {userResult.userName}
                    </div>
                    {userResult.error ? (
                      <div className="text-red-600">{userResult.error}</div>
                    ) : (
                      <div className="text-slate-600 space-y-1">
                        <div>✓ {userResult.inserted || 0} tasks created</div>
                        <div>✓ {userResult.updated || 0} tasks updated</div>
                        <div className="font-medium text-emerald-700">
                          Total: {userResult.total || 0} essay tasks
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : results.type === 'generate' ? (
            // Generate missing tasks results
            <div className="space-y-2 text-sm text-emerald-700">
              <div className="flex justify-between">
                <span>Timeline Tasks Created:</span>
                <span className="font-semibold">{results.tasksCreated || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Essays Processed:</span>
                <span className="font-semibold">{results.essaysProcessed || 0}</span>
              </div>
              {results.colleges && results.colleges.length > 0 && (
                <div className="pt-2 border-t border-emerald-200">
                  <div className="font-medium mb-1">Colleges:</div>
                  {results.colleges.map((college, idx) => (
                    <div key={idx} className="text-xs pl-2">
                      {college.name}: {college.tasksCreated} tasks for {college.essaysProcessed} essays
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : results.type === 'update_titles' ? (
            // Update task titles results
            <div className="space-y-2 text-sm text-emerald-700">
              <div className="flex justify-between">
                <span>Timeline Tasks Updated:</span>
                <span className="font-semibold">{results.tasksUpdated || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Essays Processed:</span>
                <span className="font-semibold">{results.essaysProcessed || 0}</span>
              </div>
              {results.colleges && results.colleges.length > 0 && (
                <div className="pt-2 border-t border-emerald-200">
                  <div className="font-medium mb-1">Colleges:</div>
                  {results.colleges.map((college, idx) => (
                    <div key={idx} className="text-xs pl-2">
                      {college.name}: {college.tasksUpdated} tasks updated for {college.essaysProcessed} essays
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : results.type === 'update_from_templates' ? (
            // Update all fields from templates results
            <div className="space-y-2 text-sm text-emerald-700">
              <div className="bg-rose-50 border border-rose-200 rounded p-2 mb-2">
                <div className="font-semibold text-rose-700">🔥 Full Template Update Complete</div>
                <div className="text-xs text-rose-600 mt-1">Updated: title, description, priority, module_link, workflow_key, sub_workflow_key, hierarchy_level, display_order, workflow_metadata, task_tip, and metadata</div>
              </div>
              <div className="flex justify-between">
                <span>Timeline Tasks Updated:</span>
                <span className="font-semibold">{results.tasksUpdated || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Essays Processed:</span>
                <span className="font-semibold">{results.essaysProcessed || 0}</span>
              </div>
              {results.colleges && results.colleges.length > 0 && (
                <div className="pt-2 border-t border-emerald-200">
                  <div className="font-medium mb-1">Colleges:</div>
                  {results.colleges.map((college, idx) => (
                    <div key={idx} className="text-xs pl-2">
                      {college.name}: {college.tasksUpdated} tasks updated for {college.essaysProcessed} essays
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            // Single user sync results (original)
            <div className="space-y-2 text-sm text-emerald-700">
              <div className="flex justify-between">
                <span>Tasks Created:</span>
                <span className="font-semibold">{results.created || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Tasks Updated:</span>
                <span className="font-semibold">{results.updated || 0}</span>
              </div>
              <div className="pt-2 border-t border-emerald-200">
                <div className="flex justify-between font-semibold">
                  <span>Total Essay Tasks:</span>
                  <span>{results.total || 0}</span>
                </div>
              </div>
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-emerald-200 text-xs text-emerald-600">
            Synced at {new Date(results.timestamp).toLocaleTimeString()}
          </div>
        </div>
      )}
    </div>
  );
};

export default EssayTaskSyncAdmin;
